import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';

import {
  type SnackbarAction,
  type SnackbarMessage,
  useSnackbarQueue,
} from './headless/snackbar-queue.js';

export type { SnackbarAction, SnackbarMessage } from './headless/snackbar-queue.js';

export interface SnackbarApi {
  /** Enqueues a snackbar. Undo actions must use `action.kind: 'undo'` (client-reversible only). */
  show(message: SnackbarMessage): void;
  dismiss(): void;
}

const SnackbarContext = createContext<SnackbarApi | null>(null);

function SnackbarViewport({
  onDismiss,
  onPause,
  onResume,
  snackbar,
}: {
  readonly onDismiss: () => void;
  readonly onPause: () => void;
  readonly onResume: () => void;
  readonly snackbar: NonNullable<ReturnType<typeof useSnackbarQueue>['current']>;
}) {
  const liveRef = useRef<HTMLDivElement>(null);

  return (
    <div
      aria-live="polite"
      className="saay-snackbar-region"
      onFocus={onPause}
      onMouseEnter={onPause}
      onMouseLeave={onResume}
      role="region"
    >
      <div className="saay-snackbar" ref={liveRef}>
        <div className="saay-snackbar__text">
          <div className="saay-snackbar__title">{snackbar.title}</div>
          {snackbar.description === undefined ? null : (
            <div className="saay-snackbar__description">{snackbar.description}</div>
          )}
        </div>
        {snackbar.action === undefined ? null : (
          <button
            className="saay-snackbar__action"
            onClick={() => {
              snackbar.action?.onAction();
              onDismiss();
            }}
            type="button"
          >
            {snackbar.action.label}
          </button>
        )}
      </div>
    </div>
  );
}

export function SnackbarProvider({ children }: { readonly children: ReactNode }) {
  const { current, dismiss, enqueue, pause, resume } = useSnackbarQueue();

  const show = useCallback(
    (message: SnackbarMessage) => {
      if (message.action?.kind === 'undo' && message.action.label === '') {
        throw new Error('Undo snackbar actions need a label.');
      }
      enqueue(message);
    },
    [enqueue],
  );

  const api = useMemo<SnackbarApi>(() => ({ dismiss, show }), [dismiss, show]);

  return (
    <SnackbarContext.Provider value={api}>
      {children}
      {current === null ? null : (
        <SnackbarViewport
          onDismiss={dismiss}
          onPause={pause}
          onResume={resume}
          snackbar={current}
        />
      )}
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarApi {
  const api = useContext(SnackbarContext);
  if (api === null) {
    throw new Error('useSnackbar must be used inside <UiProvider>.');
  }
  return api;
}
